import {Workflow, WorkflowExecution, WorkflowExecutionStatus} from '@amzn/finspace-service-typescript-client';
import {ActivityItem} from '../components/AllActivities/types';
import {ActivityStatus} from "src/components/Dashboard/ActivityTable/types";
import {WORKFLOW_TAGS} from '../constants/workflow-tags';

/**
 * Maps WorkflowExecutionStatus to ActivityItem status
 */
export const mapExecutionStatusToActivityStatus = (status: WorkflowExecutionStatus): ActivityStatus => {
  switch (status) {
    case WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED:
    case WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED:
      return ActivityStatus.COMPLETED;
    case WorkflowExecutionStatus.AGENT_EXECUTION_PENDING:
    case WorkflowExecutionStatus.AGENT_EXECUTION_IN_PROGRESS:
    case WorkflowExecutionStatus.REPORT_GENERATION_PENDING:
    case WorkflowExecutionStatus.REPORT_GENERATION_IN_PROGRESS:
    case WorkflowExecutionStatus.USER_INPUT_REQUIRED:
      return ActivityStatus.IN_PROGRESS;
    case WorkflowExecutionStatus.AGENT_EXECUTION_FAILED:
    case WorkflowExecutionStatus.REPORT_GENERATION_FAILED:
      return ActivityStatus.FAILED;
    case WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT:
      return ActivityStatus.TIMED_OUT
    default:
      return ActivityStatus.FAILED; // Default unknown statuses to Failed
  }
};

/**
 * Derives category from workflow tags by finding the first valid tag from WORKFLOW_TAGS
 */
export const deriveCategory = (tags?: string[]): string | undefined => {
  if (!tags || tags.length === 0) return undefined;
  
  // Get all valid workflow tag values from our centralized definition
  const validTags: string[] = Object.values(WORKFLOW_TAGS).map(wf => wf.value);
  
  // Find the first tag that matches a valid workflow tag
  const matchedTag = tags.find(tag => validTags.includes(tag));
  
  return matchedTag || tags[0];
};

/**
 * Formats date for display
 */
export const formatExecutionDate = (startTime?: string | Date, endTime?: string | Date): string => {
  const date = endTime || startTime;
  if (!date) return 'N/A';
  
  try {
    return new Date(date).toLocaleString(undefined, {
      timeZoneName: 'short'
    });
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Transforms WorkflowExecution and Workflow to ActivityItem
 */
export const transformToActivityItem = (
  execution: WorkflowExecution,
  workflow?: Workflow
): ActivityItem => {
  const category = deriveCategory(workflow?.tags);
  
  return {
    id: execution.id!,
    workflowId: execution.workflowId!,
    workflowExecutionId: execution.id!, // Add workflowExecutionId for delete functionality
    spaceId: execution.spaceId,
    reportName: workflow?.name || 'Unknown Workflow',
    description: workflow?.description || 'No description available',
    date: formatExecutionDate(execution.startTime, execution.endTime),
    status: mapExecutionStatusToActivityStatus(execution.status!),
    category: category || 'Other',
    completedAt: execution.endTime ? new Date(execution.endTime).toISOString() : undefined,
    createdAt: execution.startTime ? new Date(execution.startTime).toISOString() : undefined,
    actions: {
      viewUrl: `/reports-and-analysis/${category}/${execution.workflowId}/report/${execution.id}` // Dynamic URL based on first tag
    }
  };
};
